import React from 'react';
import { Language } from '../../pages/DevPortal';
import { AlertTriangle } from 'lucide-react';

export default function FhirSection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';

    return (
        <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {isEn ? 'HL7 FHIR R4 Integration Mapping' : 'Mapeo de Integración HL7 FHIR R4'}
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {isEn 
                    ? 'Hospital Status tracks workflow data, not clinical observations. The mapping focuses on patient logistics and encounter milestones.'
                    : 'Hospital Status rastrea datos de flujo de trabajo, no observaciones clínicas. El mapeo se enfoca en logística y progreso quirúrgico.'}
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-10 flex gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
                <div>
                    <h4 className="font-bold text-amber-900 text-sm">Target Integration Architecture</h4>
                    <p className="text-sm text-amber-800 mt-1">
                        {isEn 
                            ? 'Hospital Status exposes REST/JSON APIs, not native FHIR. Integration with systems like Philips TASI requires an adapter layer (Middleware) inside the hospital net.'
                            : 'Exponemos REST/JSON. Integración con sistemas como TASI requiere una capa middleware (adapter) dentro de la red del hospital.'}
                    </p>
                </div>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4">Resource Mappings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                
                <div className="border border-slate-200 rounded-lg p-5">
                    <h3 className="font-bold text-indigo-700 bg-indigo-50 inline-block px-2 py-1 rounded text-sm mb-3">Encounter</h3>
                    <p className="text-sm text-slate-600 mb-4">Maps to the surgical patient lifecycle (admission to discharge).</p>
                    <ul className="text-sm text-slate-600 space-y-2">
                        <li><code className="bg-slate-100 px-1 rounded">status</code> ← Generated from patient status</li>
                        <li><code className="bg-slate-100 px-1 rounded">period.start</code> ← <code className="text-indigo-600">admissionTime</code></li>
                        <li><code className="bg-slate-100 px-1 rounded">location</code> ← <code className="text-indigo-600">room</code></li>
                    </ul>
                </div>

                <div className="border border-slate-200 rounded-lg p-5">
                    <h3 className="font-bold text-indigo-700 bg-indigo-50 inline-block px-2 py-1 rounded text-sm mb-3">Procedure</h3>
                    <p className="text-sm text-slate-600 mb-4">Maps to the surgery details and completion.</p>
                    <ul className="text-sm text-slate-600 space-y-2">
                        <li><code className="bg-slate-100 px-1 rounded">code.text</code> ← <code className="text-indigo-600">procedureName</code></li>
                        <li><code className="bg-slate-100 px-1 rounded">performer.actor</code> ← <code className="text-indigo-600">doctorName</code></li>
                        <li><code className="bg-slate-100 px-1 rounded">complication</code> ← <code className="text-indigo-600">currentCondition</code></li>
                    </ul>
                </div>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4">Status Translations</h2>
            <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
                <table className="w-full text-left border-collapse text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-4 py-3 font-semibold text-slate-900">Platform Status</th>
                            <th className="px-4 py-3 font-semibold text-slate-900">FHIR Encounter.status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-600">
                        <tr><td className="px-4 py-2 font-mono text-xs">WAITING</td><td className="px-4 py-2 font-mono text-xs">arrived</td></tr>
                        <tr><td className="px-4 py-2 font-mono text-xs">PREPARATION</td><td className="px-4 py-2 font-mono text-xs">triaged</td></tr>
                        <tr><td className="px-4 py-2 font-mono text-xs text-indigo-600">ROOM_ENTRY</td><td className="px-4 py-2 font-mono text-xs text-indigo-600">in-progress</td></tr>
                        <tr><td className="px-4 py-2 font-mono text-xs text-indigo-600">ANESTHESIA</td><td className="px-4 py-2 font-mono text-xs text-indigo-600">in-progress</td></tr>
                        <tr><td className="px-4 py-2 font-mono text-xs text-indigo-600">SURGERY</td><td className="px-4 py-2 font-mono text-xs text-indigo-600">in-progress</td></tr>
                        <tr><td className="px-4 py-2 font-mono text-xs">RECOVERY</td><td className="px-4 py-2 font-mono text-xs">onleave</td></tr>
                        <tr><td className="px-4 py-2 font-mono text-xs">EXIT</td><td className="px-4 py-2 font-mono text-xs">finished</td></tr>
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-slate-400 mt-3 italic">
                {isEn ? '* Note: Granular surgery milestones (Incision, Progress %) map to Encounter custom extensions.' : '* Nota: Los porcentajes dentro de cirugía mapean a extensiones propias.'}
            </p>
        </div>
    );
}
